// react
import { useRef, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Alert,
	alpha,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	Add as IconAdd,
	ArrowBack as IconBack,
	ArrowForward as IconNext,
	Assignment as IconActivity,
	AutoAwesome as IconAI,
	Check as IconCheck,
	CheckCircle as IconCheckCircle,
	Description as IconContract,
	ExpandLess as IconExpandLess,
	ExpandMore as IconExpandMore,
	Gavel as IconCondition,
	Assignment as IconWorkPlan,
	PlaylistAddCheck as IconConditions,
	Receipt as IconReceipt,
} from '@mui/icons-material';

// context

// data
import {
	ActivityForm,
	ConditionForm,
	ContractOption,
	mockWizardAccounts,
	mockWizardActivities,
	mockWizardWorkPlans,
	StepConfig,
	WizardStep,
	WizardValues,
} from '../../../data';

// utils
import { formatCurrency } from '../../../utils/currency';

// Constants

const STEPS: StepConfig[] = [
	{
		key: 'SELECT_ACCOUNT',
		label: 'Select Account',
		description: 'Choose the designated account to plan spend from',
		icon: <IconAccount />,
	},
	{
		key: 'SELECT_WORK_PLAN',
		label: 'Work Plan',
		description: 'Select or create a work plan',
		icon: <IconWorkPlan />,
	},
	{
		key: 'CREATE_ACTIVITY',
		label: 'Activity',
		description: 'Define the activity or work item',
		icon: <IconActivity />,
	},
	{
		key: 'LINK_CONTRACT',
		label: 'Contract',
		description: 'Link to an existing contract or skip',
		icon: <IconContract />,
	},
	{
		key: 'SET_CONDITIONS',
		label: 'Conditions',
		description: 'Set payment conditions and milestones',
		icon: <IconConditions />,
	},
	{
		key: 'REVIEW',
		label: 'Review',
		description: 'Review and confirm your spend plan',
		icon: <IconCheck />,
	},
];

// Mock contracts
const MOCK_CONTRACTS: ContractOption[] = [
	{
		id: 1,
		num: 'CW-2025-001',
		supplierName: 'ABC Construction Ltd',
		amount: 1500000,
		currency: 'USD',
	},
	{
		id: 2,
		num: 'CS-2025-002',
		supplierName: 'XYZ Engineering Consultants',
		amount: 350000,
		currency: 'USD',
	},
];

// Mock entities (responsible parties)
const MOCK_ENTITIES = [
	{ id: 1, name: 'PIU - Metro Manila' },
	{ id: 2, name: 'Sub-PIU North' },
	{ id: 3, name: 'Sub-PIU South' },
];

// AI Condition suggestion chips
const AI_SUGGESTION_CHIPS = [
	'Document requirements for payments over $10,000',
	'Environmental compliance certificates',
	'Multi-level approval workflow',
	'Quarterly progress report submissions',
	'Budget threshold alerts',
];

// Component

export const PageWizard = () => {
	// hooks
	const theme = useTheme();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// context

	// initial account from URL params (if any)
	const initialAccountId = searchParams.get('account')
		? Number(searchParams.get('account'))
		: undefined;

	// state
	const [activeStep, setActiveStep] = useState(0);
	const [values, setValues] = useState<WizardValues>({
		// Account
		accountId: initialAccountId || '',
		selectedAccount: initialAccountId
			? mockWizardAccounts.find((a) => a.id === initialAccountId) || null
			: null,
		// Work Plan
		workPlanId: '',
		selectedWorkPlan: null,
		newWorkPlanName: '',
		newWorkPlanDescription: '',
		newWorkPlanPlannedAmount: '',
		// Activity
		activityId: '',
		selectedActivity: null,
		activity: {
			name: '',
			description: '',
			plannedAmount: '',
			responsibleEntityId: '',
			startDate: '',
			endDate: '',
		},
		// Contract
		contractId: '',
		selectedContract: null,
		// Conditions
		conditions: [],
	});
	const [error, setError] = useState<string | null>(null);
	const [showAiPanel, setShowAiPanel] = useState(false);
	const [aiPrompt, setAiPrompt] = useState('');

	// refs
	const contentRef = useRef<HTMLDivElement>(null);

	// helpers
	const scrollToTop = () => {
		contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// ==========================================================================
	// Step Validation & Navigation Logic
	// ==========================================================================

	const isStepComplete = (stepKey: WizardStep): boolean => {
		switch (stepKey) {
			case 'SELECT_ACCOUNT':
				return !!values.accountId;

			case 'SELECT_WORK_PLAN':
				if (values.workPlanId === 'NEW') {
					return !!(
						values.newWorkPlanName.trim() &&
						values.newWorkPlanPlannedAmount &&
						values.newWorkPlanPlannedAmount > 0
					);
				}
				return values.workPlanId !== '';

			case 'CREATE_ACTIVITY':
				return !!(
					values.activityId &&
					(values.activityId !== 'NEW' ||
						(values.activity.name.trim() &&
							values.activity.plannedAmount &&
							values.activity.plannedAmount > 0))
				);

			case 'LINK_CONTRACT':
				return values.contractId !== '';

			case 'SET_CONDITIONS':
				return values.conditions.length > 0;

			case 'REVIEW':
				return isStepComplete('SELECT_ACCOUNT') && isStepComplete('SELECT_WORK_PLAN');

			default:
				return false;
		}
	};

	const canNavigateToStep = (stepKey: WizardStep): boolean => {
		const targetIndex = STEPS.findIndex((s) => s.key === stepKey);
		if (targetIndex === 0) return true;

		switch (stepKey) {
			case 'SELECT_ACCOUNT':
				return true;

			case 'SELECT_WORK_PLAN':
				return isStepComplete('SELECT_ACCOUNT');

			case 'CREATE_ACTIVITY':
				return isStepComplete('SELECT_ACCOUNT') && isStepComplete('SELECT_WORK_PLAN');

			case 'LINK_CONTRACT':
				return (
					isStepComplete('SELECT_ACCOUNT') &&
					isStepComplete('SELECT_WORK_PLAN') &&
					isStepComplete('CREATE_ACTIVITY')
				);

			case 'SET_CONDITIONS':
				return (
					isStepComplete('SELECT_ACCOUNT') &&
					isStepComplete('SELECT_WORK_PLAN') &&
					isStepComplete('CREATE_ACTIVITY') &&
					isStepComplete('LINK_CONTRACT')
				);

			case 'REVIEW':
				return isStepComplete('SELECT_ACCOUNT') && isStepComplete('SELECT_WORK_PLAN');

			default:
				return false;
		}
	};

	// derived
	const currentStepConfig = STEPS[activeStep];
	const isFirstStep = activeStep === 0;
	const isLastStep = activeStep === STEPS.length - 1;
	const canSkipToReview = canNavigateToStep('REVIEW') && activeStep < STEPS.length - 1;

	// handlers
	const handleNext = () => {
		setError(null);

		if (!validateStep()) {
			scrollToTop();
			return;
		}

		if (isLastStep) {
			if (!isStepComplete('SELECT_ACCOUNT') || !isStepComplete('SELECT_WORK_PLAN')) {
				setError('Account and Work Plan are required to create a spend plan');
				scrollToTop();
				return;
			}
			// Handle completion - could navigate or show success
			handleReset();
		} else {
			const nextStep = activeStep + 1;
			if (nextStep < STEPS.length && canNavigateToStep(STEPS[nextStep].key)) {
				setActiveStep(nextStep);
				scrollToTop();
			}
		}
	};

	const handleBack = () => {
		setError(null);
		if (!isFirstStep) {
			let previousStep = activeStep - 1;
			while (previousStep >= 0 && !canNavigateToStep(STEPS[previousStep].key)) {
				previousStep--;
			}
			if (previousStep >= 0) {
				setActiveStep(previousStep);
				scrollToTop();
			}
		}
	};

	const handleSkipToReview = () => {
		setError(null);
		if (!canNavigateToStep('REVIEW')) {
			setError('Please complete Account and Work Plan selection before reviewing');
			scrollToTop();
			return;
		}

		const currentStepKey = STEPS[activeStep].key;

		if (currentStepKey === 'SELECT_WORK_PLAN' && values.workPlanId) {
			setValues({
				...values,
				workPlanId: '',
				selectedWorkPlan: null,
				newWorkPlanName: '',
				newWorkPlanDescription: '',
				newWorkPlanPlannedAmount: '',
			});
		} else if (currentStepKey === 'CREATE_ACTIVITY' && values.activityId) {
			setValues({
				...values,
				activityId: '',
				selectedActivity: null,
				activity: {
					name: '',
					description: '',
					plannedAmount: '',
					responsibleEntityId: '',
					startDate: '',
					endDate: '',
				},
			});
		} else if (currentStepKey === 'LINK_CONTRACT' && values.contractId) {
			setValues({
				...values,
				contractId: '',
				selectedContract: null,
			});
		}

		setActiveStep(STEPS.length - 1);
		scrollToTop();
	};

	const handleStepClick = (stepIndex: number) => {
		setError(null);
		const targetStep = STEPS[stepIndex];
		if (canNavigateToStep(targetStep.key)) {
			setActiveStep(stepIndex);
			scrollToTop();
		}
	};

	const handleReset = () => {
		setActiveStep(0);
		setValues({
			accountId: initialAccountId || '',
			selectedAccount: initialAccountId
				? mockWizardAccounts.find((a) => a.id === initialAccountId) || null
				: null,
			workPlanId: '',
			selectedWorkPlan: null,
			newWorkPlanName: '',
			newWorkPlanDescription: '',
			newWorkPlanPlannedAmount: '',
			activityId: '',
			selectedActivity: null,
			activity: {
				name: '',
				description: '',
				plannedAmount: '',
				responsibleEntityId: '',
				startDate: '',
				endDate: '',
			},
			contractId: '',
			selectedContract: null,
			conditions: [],
		});
		setError(null);
	};

	const validateStep = (): boolean => {
		switch (currentStepConfig.key) {
			case 'SELECT_ACCOUNT':
				if (!values.accountId) {
					setError('Please select a designated account');
					return false;
				}
				return true;

			case 'SELECT_WORK_PLAN':
				if (!values.workPlanId) {
					setError('Please select or create a work plan');
					return false;
				}
				if (values.workPlanId === 'NEW') {
					if (!values.newWorkPlanName.trim()) {
						setError('Please enter a name for the new work plan');
						return false;
					}
					if (!values.newWorkPlanPlannedAmount || values.newWorkPlanPlannedAmount <= 0) {
						setError('Please enter a valid planned amount');
						return false;
					}
				}
				return true;

			case 'CREATE_ACTIVITY':
				if (!values.activityId) {
					setError('Please select or create an activity');
					return false;
				}
				if (values.activityId === 'NEW') {
					if (!values.activity.name.trim()) {
						setError('Please enter an activity name');
						return false;
					}
					if (!values.activity.plannedAmount || values.activity.plannedAmount <= 0) {
						setError('Please enter a valid planned amount');
						return false;
					}
				}
				return true;

			case 'LINK_CONTRACT':
				return true;

			case 'SET_CONDITIONS':
				if (values.conditions.length > 0) {
					const totalPercentage = values.conditions.reduce(
						(sum, c) => sum + c.paymentPercentage,
						0
					);
					if (totalPercentage > 100) {
						setError('Total payment percentage cannot exceed 100%');
						return false;
					}
				}
				return true;

			case 'REVIEW':
				return true;

			default:
				return true;
		}
	};

	const handleAccountSelect = (accountId: number) => {
		const account = mockWizardAccounts.find((a) => a.id === accountId);
		setValues((prev) => ({
			...prev,
			accountId,
			selectedAccount: account || null,
		}));
	};

	const handleWorkPlanSelect = (workPlanId: number | 'NEW') => {
		const workPlan =
			workPlanId === 'NEW' ? null : mockWizardWorkPlans.find((e) => e.id === workPlanId) || null;
		setValues((prev) => ({
			...prev,
			workPlanId,
			selectedWorkPlan: workPlan,
		}));
	};

	const handleActivitySelect = (activityId: number | 'NEW') => {
		const activity =
			activityId === 'NEW' ? null : mockWizardActivities.find((a) => a.id === activityId) || null;

		if (activity) {
			setValues((prev) => ({
				...prev,
				activityId,
				selectedActivity: activity,
				activity: {
					name: activity.name,
					description: activity.description || '',
					plannedAmount: activity.plannedAmount,
					responsibleEntityId: activity.responsibleEntityId || '',
					startDate: activity.startDate || '',
					endDate: activity.endDate || '',
				},
			}));
		} else {
			setValues((prev) => ({
				...prev,
				activityId: 'NEW',
				selectedActivity: null,
				activity: {
					name: '',
					description: '',
					plannedAmount: '',
					responsibleEntityId: '',
					startDate: '',
					endDate: '',
				},
			}));
		}
	};

	const handleContractSelect = (contractId: number | 'NEW' | 'SKIP') => {
		const contract =
			contractId === 'NEW' || contractId === 'SKIP'
				? null
				: MOCK_CONTRACTS.find((c) => c.id === contractId) || null;
		setValues((prev) => ({
			...prev,
			contractId,
			selectedContract: contract,
		}));
	};

	const handleActivityChange = (field: keyof ActivityForm, value: string | number) => {
		setValues((prev) => ({
			...prev,
			activity: {
				...prev.activity,
				[field]: value,
			},
		}));
	};

	const handleAddCondition = () => {
		setValues((prev) => ({
			...prev,
			conditions: [
				...prev.conditions,
				{
					name: '',
					description: '',
					paymentPercentage: 0,
					isRequired: true,
				},
			],
		}));
	};

	const handleConditionChange = (
		index: number,
		field: keyof ConditionForm,
		value: string | number | boolean | null
	) => {
		setValues((prev) => ({
			...prev,
			conditions: prev.conditions.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
		}));
	};

	const handleRemoveCondition = (index: number) => {
		setValues((prev) => ({
			...prev,
			conditions: prev.conditions.filter((_, i) => i !== index),
		}));
	};

	// ==========================================================================
	// Render Steps
	// ==========================================================================

	const renderAccountStep = () => (
		<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />}>
			{mockWizardAccounts.map((account) => {
				const isSelected = values.accountId === account.id;
				return (
					<Box
						key={account.id}
						onClick={() => handleAccountSelect(account.id)}
						sx={{
							bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
							cursor: 'pointer',
							px: 3,
							py: 2,
							transition: 'all 0.15s ease',
							'&:hover': {
								bgcolor: alpha(theme.palette.grey[500], 0.04),
							},
						}}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Box
								sx={{
									alignItems: 'center',
									border: isSelected
										? 'none'
										: `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
									bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
									borderRadius: '50%',
									color: 'primary.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									transition: 'all 0.15s ease',
									width: 40,
								}}>
								<IconAccount sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="body1" fontWeight={isSelected ? 600 : 500} noWrap>
									{account.name}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{account.num} • {account.loanCode}
								</Typography>
							</Box>
							<Box sx={{ textAlign: 'right' }}>
								<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
									Available
								</Typography>
								<Typography variant="body1" fontWeight={600} color="text.primary">
									{formatCurrency({ currency: account.currency, value: account.balance })}
								</Typography>
							</Box>
							{isSelected && (
								<IconCheckCircle
									sx={{ color: alpha(theme.palette.primary.main, 0.7), fontSize: 20 }}
								/>
							)}
						</Stack>
					</Box>
				);
			})}
		</Stack>
	);

	const renderWorkPlanStep = () => (
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: 'text.disabled',
					display: 'block',
					mb: 1,
					px: 3,
					textTransform: 'uppercase',
					letterSpacing: 0.5,
				}}>
				{t('spendPlan.existingWorkPlans')}
			</Typography>
			<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />}>
				{mockWizardWorkPlans.map((workPlan) => {
					const isSelected = values.workPlanId === workPlan.id;
					return (
						<Box
							key={workPlan.id}
							onClick={() => handleWorkPlanSelect(workPlan.id as number)}
							sx={{
								bgcolor: isSelected ? alpha(theme.palette.warning.main, 0.03) : 'transparent',
								cursor: 'pointer',
								px: 3,
								py: 2,
								transition: 'all 0.15s ease',
								'&:hover': {
									bgcolor: alpha(theme.palette.grey[500], 0.04),
								},
							}}>
							<Stack direction="row" alignItems="center" spacing={2}>
								<Box
									sx={{
										alignItems: 'center',
										border: isSelected
											? 'none'
											: `1.5px solid ${alpha(theme.palette.warning.main, 0.3)}`,
										bgcolor: isSelected ? alpha(theme.palette.warning.main, 0.12) : 'transparent',
										borderRadius: '50%',
										color: 'warning.main',
										display: 'flex',
										height: 40,
										justifyContent: 'center',
										transition: 'all 0.15s ease',
										width: 40,
									}}>
									<IconWorkPlan sx={{ fontSize: 20 }} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body1" fontWeight={isSelected ? 600 : 500} noWrap>
										{workPlan.name}
									</Typography>
									{workPlan.fiscalYear && (
										<Typography variant="body2" color="text.secondary">
											{workPlan.fiscalYear}
										</Typography>
									)}
								</Box>
								<Stack direction="row" spacing={3} sx={{ textAlign: 'right' }}>
									<Box>
										<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
											Available
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{formatCurrency({
												currency: workPlan.currency,
												value: workPlan.availableAmount,
											})}
										</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
											Planned
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{formatCurrency({
												currency: workPlan.currency,
												value: workPlan.plannedAmount,
											})}
										</Typography>
									</Box>
								</Stack>
								{isSelected && (
									<IconCheckCircle
										sx={{ color: alpha(theme.palette.warning.main, 0.7), fontSize: 20 }}
									/>
								)}
							</Stack>
						</Box>
					);
				})}
			</Stack>

			<Divider sx={{ my: 2, mx: 3 }} />

			{/* Create New */}
			<Box
				onClick={() => handleWorkPlanSelect('NEW')}
				sx={{
					bgcolor:
						values.workPlanId === 'NEW' ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
					cursor: 'pointer',
					px: 3,
					py: 2,
					transition: 'all 0.15s ease',
					'&:hover': {
						bgcolor: alpha(theme.palette.grey[500], 0.04),
					},
				}}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Box
						sx={{
							alignItems: 'center',
							border:
								values.workPlanId === 'NEW'
									? 'none'
									: `1.5px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
							bgcolor:
								values.workPlanId === 'NEW'
									? alpha(theme.palette.primary.main, 0.12)
									: 'transparent',
							borderRadius: '50%',
							color: 'primary.main',
							display: 'flex',
							height: 40,
							justifyContent: 'center',
							transition: 'all 0.15s ease',
							width: 40,
						}}>
						<IconAdd sx={{ fontSize: 20 }} />
					</Box>
					<Box>
						<Typography variant="body1" fontWeight={values.workPlanId === 'NEW' ? 600 : 500}>
							{t('spendPlan.createNewWorkPlan')}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t('spendPlan.startNewWorkPlan')}
						</Typography>
					</Box>
				</Stack>
			</Box>

			<Collapse in={values.workPlanId === 'NEW'}>
				<Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.02), p: 3 }}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label={t('spendPlan.workPlanName')}
								onChange={(e) =>
									setValues((prev) => ({ ...prev, newWorkPlanName: e.target.value }))
								}
								required
								size="small"
								value={values.newWorkPlanName}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
								label="Planned Amount"
								onChange={(e) =>
									setValues((prev) => ({
										...prev,
										newWorkPlanPlannedAmount: Number(e.target.value),
									}))
								}
								required
								size="small"
								type="number"
								value={values.newWorkPlanPlannedAmount}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Description (Optional)"
								multiline
								onChange={(e) =>
									setValues((prev) => ({ ...prev, newWorkPlanDescription: e.target.value }))
								}
								rows={2}
								size="small"
								value={values.newWorkPlanDescription}
							/>
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</Box>
	);

	const renderActivityStep = () => (
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: 'text.disabled',
					display: 'block',
					mb: 1,
					px: 3,
					textTransform: 'uppercase',
					letterSpacing: 0.5,
				}}>
				Existing Activities
			</Typography>
			<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />}>
				{mockWizardActivities.map((activity) => {
					const isSelected = values.activityId === activity.id;
					return (
						<Box
							key={activity.id}
							onClick={() => handleActivitySelect(activity.id as number)}
							sx={{
								bgcolor: isSelected ? alpha(theme.palette.info.main, 0.03) : 'transparent',
								cursor: 'pointer',
								px: 3,
								py: 2,
								transition: 'all 0.15s ease',
								'&:hover': {
									bgcolor: alpha(theme.palette.grey[500], 0.04),
								},
							}}>
							<Stack direction="row" alignItems="center" spacing={2}>
								<Box
									sx={{
										alignItems: 'center',
										border: isSelected
											? 'none'
											: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
										bgcolor: isSelected ? alpha(theme.palette.info.main, 0.12) : 'transparent',
										borderRadius: '50%',
										color: 'info.main',
										display: 'flex',
										height: 40,
										justifyContent: 'center',
										transition: 'all 0.15s ease',
										width: 40,
									}}>
									<IconActivity sx={{ fontSize: 20 }} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body1" fontWeight={isSelected ? 600 : 500} noWrap>
										{activity.name}
									</Typography>
									{activity.description && (
										<Typography variant="body2" color="text.secondary" noWrap>
											{activity.description}
										</Typography>
									)}
								</Box>
								<Box sx={{ textAlign: 'right' }}>
									<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
										Planned
									</Typography>
									<Typography variant="body2" fontWeight={500}>
										{formatCurrency({ currency: 'USD', value: activity.plannedAmount })}
									</Typography>
								</Box>
								{isSelected && (
									<IconCheckCircle
										sx={{ color: alpha(theme.palette.info.main, 0.7), fontSize: 20 }}
									/>
								)}
							</Stack>
						</Box>
					);
				})}
			</Stack>

			<Divider sx={{ my: 2, mx: 3 }} />

			{/* Create New */}
			<Box
				onClick={() => handleActivitySelect('NEW')}
				sx={{
					bgcolor:
						values.activityId === 'NEW' ? alpha(theme.palette.info.main, 0.03) : 'transparent',
					cursor: 'pointer',
					px: 3,
					py: 2,
					transition: 'all 0.15s ease',
					'&:hover': {
						bgcolor: alpha(theme.palette.grey[500], 0.04),
					},
				}}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Box
						sx={{
							alignItems: 'center',
							border:
								values.activityId === 'NEW'
									? 'none'
									: `1.5px dashed ${alpha(theme.palette.info.main, 0.4)}`,
							bgcolor:
								values.activityId === 'NEW' ? alpha(theme.palette.info.main, 0.12) : 'transparent',
							borderRadius: '50%',
							color: 'info.main',
							display: 'flex',
							height: 40,
							justifyContent: 'center',
							transition: 'all 0.15s ease',
							width: 40,
						}}>
						<IconAdd sx={{ fontSize: 20 }} />
					</Box>
					<Box>
						<Typography variant="body1" fontWeight={values.activityId === 'NEW' ? 600 : 500}>
							Create New Activity
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Define a new work item or task
						</Typography>
					</Box>
				</Stack>
			</Box>

			<Collapse in={values.activityId === 'NEW'}>
				<Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.02), p: 3 }}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Activity Name"
								onChange={(e) => handleActivityChange('name', e.target.value)}
								required
								size="small"
								value={values.activity.name}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
								label="Planned Amount"
								onChange={(e) => handleActivityChange('plannedAmount', Number(e.target.value))}
								required
								size="small"
								type="number"
								value={values.activity.plannedAmount}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth size="small">
								<InputLabel>Responsible Entity</InputLabel>
								<Select
									label="Responsible Entity"
									onChange={(e) =>
										handleActivityChange('responsibleEntityId', e.target.value as string)
									}
									value={values.activity.responsibleEntityId}>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{MOCK_ENTITIES.map((entity) => (
										<MenuItem key={entity.id} value={entity.id}>
											{entity.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={6}>
							<Stack direction="row" spacing={2}>
								<TextField
									fullWidth
									InputLabelProps={{ shrink: true }}
									label="Start Date"
									onChange={(e) => handleActivityChange('startDate', e.target.value)}
									size="small"
									type="date"
									value={values.activity.startDate}
								/>
								<TextField
									fullWidth
									InputLabelProps={{ shrink: true }}
									label="End Date"
									onChange={(e) => handleActivityChange('endDate', e.target.value)}
									size="small"
									type="date"
									value={values.activity.endDate}
								/>
							</Stack>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Description (Optional)"
								multiline
								onChange={(e) => handleActivityChange('description', e.target.value)}
								rows={2}
								size="small"
								value={values.activity.description}
							/>
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</Box>
	);

	const renderContractStep = () => (
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: 'text.disabled',
					display: 'block',
					mb: 1,
					px: 3,
					textTransform: 'uppercase',
					letterSpacing: 0.5,
				}}>
				Available Contracts
			</Typography>
			<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />}>
				{MOCK_CONTRACTS.map((contract) => {
					const isSelected = values.contractId === contract.id;
					return (
						<Box
							key={contract.id}
							onClick={() => handleContractSelect(contract.id)}
							sx={{
								bgcolor: isSelected ? alpha(theme.palette.success.main, 0.03) : 'transparent',
								cursor: 'pointer',
								px: 3,
								py: 2,
								transition: 'all 0.15s ease',
								'&:hover': {
									bgcolor: alpha(theme.palette.grey[500], 0.04),
								},
							}}>
							<Stack direction="row" alignItems="center" spacing={2}>
								<Box
									sx={{
										alignItems: 'center',
										border: isSelected
											? 'none'
											: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
										bgcolor: isSelected ? alpha(theme.palette.success.main, 0.12) : 'transparent',
										borderRadius: '50%',
										color: 'success.main',
										display: 'flex',
										height: 40,
										justifyContent: 'center',
										transition: 'all 0.15s ease',
										width: 40,
									}}>
									<IconContract sx={{ fontSize: 20 }} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body1" fontWeight={isSelected ? 600 : 500} noWrap>
										{contract.num}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{contract.supplierName}
									</Typography>
								</Box>
								<Box sx={{ textAlign: 'right' }}>
									<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
										Value
									</Typography>
									<Typography variant="body2" fontWeight={500}>
										{formatCurrency({ currency: contract.currency, value: contract.amount || 0 })}
									</Typography>
								</Box>
								{isSelected && (
									<IconCheckCircle
										sx={{ color: alpha(theme.palette.success.main, 0.7), fontSize: 20 }}
									/>
								)}
							</Stack>
						</Box>
					);
				})}
			</Stack>

			<Divider sx={{ my: 2, mx: 3 }} />

			{/* Skip option */}
			<Box
				onClick={() => handleContractSelect('SKIP')}
				sx={{
					bgcolor:
						values.contractId === 'SKIP' ? alpha(theme.palette.grey[500], 0.04) : 'transparent',
					cursor: 'pointer',
					px: 3,
					py: 2,
					transition: 'all 0.15s ease',
					'&:hover': {
						bgcolor: alpha(theme.palette.grey[500], 0.04),
					},
				}}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Box
						sx={{
							alignItems: 'center',
							border:
								values.contractId === 'SKIP'
									? 'none'
									: `1.5px dashed ${alpha(theme.palette.grey[500], 0.4)}`,
							bgcolor:
								values.contractId === 'SKIP' ? alpha(theme.palette.grey[500], 0.12) : 'transparent',
							borderRadius: '50%',
							color: 'text.secondary',
							display: 'flex',
							height: 40,
							justifyContent: 'center',
							transition: 'all 0.15s ease',
							width: 40,
						}}>
						<IconNext sx={{ fontSize: 20 }} />
					</Box>
					<Box>
						<Typography variant="body1" fontWeight={values.contractId === 'SKIP' ? 600 : 500}>
							Skip for Now
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Continue without linking a contract
						</Typography>
					</Box>
				</Stack>
			</Box>
		</Box>
	);

	const renderConditionsStep = () => (
		<Box>
			{/* AI Suggestions - Compact collapsible */}
			<Box
				sx={{
					bgcolor: showAiPanel ? alpha(theme.palette.secondary.main, 0.02) : 'transparent',
					cursor: 'pointer',
					mx: 3,
					mb: 2,
					px: 2,
					py: 1.5,
					borderRadius: 1,
					transition: 'all 0.15s ease',
					'&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.04) },
				}}
				onClick={() => setShowAiPanel(!showAiPanel)}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<Box
							sx={{
								alignItems: 'center',
								border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
								borderRadius: '50%',
								color: 'secondary.main',
								display: 'flex',
								height: 32,
								justifyContent: 'center',
								width: 32,
							}}>
							<IconAI sx={{ fontSize: 16 }} />
						</Box>
						<Box>
							<Typography variant="body2" fontWeight={500}>
								AI-Powered Suggestions
							</Typography>
							<Typography variant="caption" color="text.disabled">
								Get intelligent recommendations
							</Typography>
						</Box>
					</Stack>
					{showAiPanel ? (
						<IconExpandLess sx={{ color: 'text.secondary', fontSize: 20 }} />
					) : (
						<IconExpandMore sx={{ color: 'text.secondary', fontSize: 20 }} />
					)}
				</Stack>
			</Box>
			<Collapse in={showAiPanel}>
				<Box
					sx={{
						bgcolor: alpha(theme.palette.grey[500], 0.02),
						mb: 2,
						mx: 3,
						px: 2,
						py: 2,
						borderRadius: 1,
					}}>
					<TextField
						fullWidth
						multiline
						rows={2}
						placeholder="e.g., I need conditions for a construction project with milestone-based payments..."
						value={aiPrompt}
						onChange={(e) => setAiPrompt(e.target.value)}
						size="small"
						sx={{ mb: 2 }}
					/>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{AI_SUGGESTION_CHIPS.map((chip) => (
							<Chip
								key={chip}
								label={chip}
								size="small"
								onClick={() => setAiPrompt(chip)}
								sx={{
									bgcolor: 'transparent',
									border: `1px solid ${theme.palette.divider}`,
									fontSize: 11,
									'&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.08) },
								}}
							/>
						))}
					</Stack>
				</Box>
			</Collapse>

			{/* Quick Templates - Horizontal pills */}
			<Box sx={{ mb: 3, px: 3 }}>
				<Typography
					variant="caption"
					sx={{
						color: 'text.disabled',
						display: 'block',
						mb: 1.5,
						textTransform: 'uppercase',
						letterSpacing: 0.5,
					}}>
					Quick Add Templates
				</Typography>
				<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
					{[
						{ name: 'Advance Payment', percentage: 20 },
						{ name: 'First Milestone', percentage: 30 },
						{ name: 'Final Delivery', percentage: 50 },
					].map((template) => (
						<Chip
							key={template.name}
							label={`${template.name} (${template.percentage}%)`}
							onClick={() => {
								setValues((prev) => ({
									...prev,
									conditions: [
										...prev.conditions,
										{
											description: '',
											isRequired: true,
											name: template.name,
											paymentPercentage: template.percentage,
										},
									],
								}));
							}}
							size="small"
							sx={{
								bgcolor: 'transparent',
								border: `1px solid ${theme.palette.divider}`,
								cursor: 'pointer',
								fontSize: 12,
								'&:hover': {
									bgcolor: alpha(theme.palette.grey[500], 0.08),
								},
							}}
						/>
					))}
				</Stack>
			</Box>

			{/* Added Conditions - Flat list */}
			{values.conditions.length > 0 && (
				<>
					<Typography
						variant="caption"
						sx={{
							color: 'text.disabled',
							display: 'block',
							mb: 1,
							px: 3,
							textTransform: 'uppercase',
							letterSpacing: 0.5,
						}}>
						Added Conditions ({values.conditions.length})
					</Typography>
					<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />} sx={{ mb: 2 }}>
						{values.conditions.map((condition, index) => (
							<Box key={index} sx={{ px: 3, py: 2 }}>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={12} sm={5}>
										<TextField
											fullWidth
											label="Condition Name"
											onChange={(e) => handleConditionChange(index, 'name', e.target.value)}
											size="small"
											value={condition.name}
										/>
									</Grid>
									<Grid item xs={6} sm={3}>
										<TextField
											fullWidth
											InputProps={{
												endAdornment: <InputAdornment position="end">%</InputAdornment>,
											}}
											label="Payment %"
											onChange={(e) =>
												handleConditionChange(index, 'paymentPercentage', Number(e.target.value))
											}
											size="small"
											type="number"
											value={condition.paymentPercentage}
										/>
									</Grid>
									<Grid item xs={4} sm={3}>
										<FormControl fullWidth size="small">
											<InputLabel>Required</InputLabel>
											<Select
												label="Required"
												onChange={(e) =>
													handleConditionChange(index, 'isRequired', e.target.value === 'yes')
												}
												value={condition.isRequired ? 'yes' : 'no'}>
												<MenuItem value="yes">Yes</MenuItem>
												<MenuItem value="no">No</MenuItem>
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={2} sm={1}>
										<IconButton
											onClick={() => handleRemoveCondition(index)}
											size="small"
											sx={{ color: 'text.secondary' }}>
											<IconCondition sx={{ fontSize: 18 }} />
										</IconButton>
									</Grid>
								</Grid>
							</Box>
						))}
					</Stack>

					{/* Total Progress - Minimal */}
					<Box sx={{ px: 3, mb: 3 }}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 0.5 }}>
							<Typography variant="caption" color="text.secondary">
								Allocation Progress
							</Typography>
							<Typography variant="caption" fontWeight={500} color="text.secondary">
								{values.conditions.reduce((sum, c) => sum + c.paymentPercentage, 0)}%
							</Typography>
						</Stack>
						<LinearProgress
							sx={{
								height: 3,
								borderRadius: 1.5,
								bgcolor: alpha(theme.palette.grey[500], 0.08),
								'& .MuiLinearProgress-bar': {
									bgcolor: alpha(theme.palette.primary.main, 0.5),
								},
							}}
							value={Math.min(
								100,
								values.conditions.reduce((sum, c) => sum + c.paymentPercentage, 0)
							)}
							variant="determinate"
						/>
					</Box>
				</>
			)}

			{/* Add Button */}
			<Box sx={{ px: 3 }}>
				<Button fullWidth onClick={handleAddCondition} startIcon={<IconAdd />} variant="outlined">
					Add Custom Condition
				</Button>
			</Box>
		</Box>
	);

	const renderReviewStep = () => (
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: 'text.disabled',
					display: 'block',
					mb: 1,
					px: 3,
					textTransform: 'uppercase',
					letterSpacing: 0.5,
				}}>
				Review Your Selections
			</Typography>
			<Stack spacing={0} divider={<Divider sx={{ mx: 3 }} />}>
				{/* Account */}
				{values.selectedAccount && (
					<Box sx={{ px: 3, py: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={2.5}>
							<Box
								sx={{
									alignItems: 'center',
									border: `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
									borderRadius: '50%',
									color: 'primary.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									width: 40,
								}}>
								<IconAccount sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="caption" color="text.disabled">
									Account
								</Typography>
								<Typography variant="subtitle1" fontWeight={500}>
									{values.selectedAccount.name}
								</Typography>
							</Box>
							<Box sx={{ textAlign: 'right' }}>
								<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
									Balance
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{formatCurrency({
										currency: values.selectedAccount.currency,
										value: values.selectedAccount.balance,
									})}
								</Typography>
							</Box>
							<IconCheckCircle
								sx={{ color: alpha(theme.palette.success.main, 0.6), fontSize: 18 }}
							/>
						</Stack>
					</Box>
				)}

				{/* Work Plan */}
				{(values.selectedWorkPlan || values.workPlanId === 'NEW') && (
					<Box sx={{ px: 3, py: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={2.5}>
							<Box
								sx={{
									alignItems: 'center',
									border: `1.5px solid ${alpha(theme.palette.warning.main, 0.3)}`,
									borderRadius: '50%',
									color: 'warning.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									width: 40,
								}}>
								<IconWorkPlan sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="caption" color="text.disabled">
									{t('spendPlan.workPlan')}
								</Typography>
								<Typography variant="subtitle1" fontWeight={500}>
									{values.selectedWorkPlan?.name ||
										values.newWorkPlanName ||
										t('spendPlan.newWorkPlan')}
								</Typography>
							</Box>
							{values.workPlanId === 'NEW' && values.newWorkPlanPlannedAmount && (
								<Box sx={{ textAlign: 'right' }}>
									<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
										Planned
									</Typography>
									<Typography variant="body2" fontWeight={500}>
										{formatCurrency({
											currency: 'USD',
											value: values.newWorkPlanPlannedAmount as number,
										})}
									</Typography>
								</Box>
							)}
							<IconCheckCircle
								sx={{ color: alpha(theme.palette.success.main, 0.6), fontSize: 18 }}
							/>
						</Stack>
					</Box>
				)}

				{/* Activity */}
				{(values.selectedActivity || values.activityId === 'NEW') && (
					<Box sx={{ px: 3, py: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={2.5}>
							<Box
								sx={{
									alignItems: 'center',
									border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
									borderRadius: '50%',
									color: 'info.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									width: 40,
								}}>
								<IconActivity sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="caption" color="text.disabled">
									Activity
								</Typography>
								<Typography variant="subtitle1" fontWeight={500}>
									{values.selectedActivity?.name || values.activity.name || 'New Activity'}
								</Typography>
							</Box>
							{values.activity.plannedAmount && (
								<Box sx={{ textAlign: 'right' }}>
									<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
										Planned
									</Typography>
									<Typography variant="body2" fontWeight={500}>
										{formatCurrency({
											currency: 'USD',
											value: values.activity.plannedAmount as number,
										})}
									</Typography>
								</Box>
							)}
							<IconCheckCircle
								sx={{ color: alpha(theme.palette.success.main, 0.6), fontSize: 18 }}
							/>
						</Stack>
					</Box>
				)}

				{/* Contract */}
				{values.contractId && values.contractId !== 'SKIP' && (
					<Box sx={{ px: 3, py: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={2.5}>
							<Box
								sx={{
									alignItems: 'center',
									border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
									borderRadius: '50%',
									color: 'success.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									width: 40,
								}}>
								<IconContract sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="caption" color="text.disabled">
									Contract
								</Typography>
								<Typography variant="subtitle1" fontWeight={500}>
									{values.selectedContract?.num || 'New Contract'}
								</Typography>
								{values.selectedContract && (
									<Typography variant="body2" color="text.secondary">
										{values.selectedContract.supplierName}
									</Typography>
								)}
							</Box>
							<IconCheckCircle
								sx={{ color: alpha(theme.palette.success.main, 0.6), fontSize: 18 }}
							/>
						</Stack>
					</Box>
				)}

				{/* Conditions */}
				{values.conditions.length > 0 && (
					<Box sx={{ px: 3, py: 2.5 }}>
						<Stack direction="row" alignItems="flex-start" spacing={2.5}>
							<Box
								sx={{
									alignItems: 'center',
									border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
									borderRadius: '50%',
									color: 'secondary.main',
									display: 'flex',
									height: 40,
									justifyContent: 'center',
									width: 40,
								}}>
								<IconConditions sx={{ fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="caption" color="text.disabled">
									Payment Conditions
								</Typography>
								<Typography variant="subtitle1" fontWeight={500}>
									{values.conditions.length} condition(s)
								</Typography>
								<Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
									{values.conditions.map((c, i) => (
										<Chip
											key={i}
											label={`${c.name || `Condition ${i + 1}`}: ${c.paymentPercentage}%`}
											size="small"
											sx={{
												bgcolor: alpha(theme.palette.grey[500], 0.06),
												border: `1px solid ${theme.palette.divider}`,
												fontSize: 11,
											}}
										/>
									))}
								</Stack>
							</Box>
							<IconCheckCircle
								sx={{ color: alpha(theme.palette.success.main, 0.6), fontSize: 18, mt: 1 }}
							/>
						</Stack>
					</Box>
				)}
			</Stack>
		</Box>
	);

	const renderStepContent = () => {
		switch (currentStepConfig.key) {
			case 'SELECT_ACCOUNT':
				return renderAccountStep();
			case 'SELECT_WORK_PLAN':
				return renderWorkPlanStep();
			case 'CREATE_ACTIVITY':
				return renderActivityStep();
			case 'LINK_CONTRACT':
				return renderContractStep();
			case 'SET_CONDITIONS':
				return renderConditionsStep();
			case 'REVIEW':
				return renderReviewStep();
			default:
				return null;
		}
	};

	// ==========================================================================
	// Main Render
	// ==========================================================================

	const progress = ((activeStep + 1) / STEPS.length) * 100;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Light Header */}
			<Box
				sx={{
					bgcolor: 'background.paper',
					borderBottom: `1px solid ${theme.palette.divider}`,
					px: 4,
					py: 2.5,
				}}>
				<Stack direction="row" alignItems="center" spacing={3}>
					<Box
						sx={{
							alignItems: 'center',
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							borderRadius: '50%',
							color: 'primary.main',
							display: 'flex',
							height: 48,
							justifyContent: 'center',
							width: 48,
						}}>
						<IconReceipt sx={{ fontSize: 24 }} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6" fontWeight={600}>
							Plan Your Spend
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{currentStepConfig.description}
						</Typography>
					</Box>
					{/* Step indicators */}
					<Stack direction="row" spacing={0.75}>
						{STEPS.map((step, index) => {
							const isComplete = isStepComplete(step.key);
							const isCurrent = index === activeStep;
							const isAccessible = canNavigateToStep(step.key);
							return (
								<Box
									key={step.key}
									onClick={() => isAccessible && handleStepClick(index)}
									sx={{
										alignItems: 'center',
										bgcolor: isCurrent
											? 'primary.main'
											: isComplete
												? alpha(theme.palette.success.main, 0.15)
												: alpha(theme.palette.grey[500], 0.1),
										borderRadius: '50%',
										color: isCurrent ? 'white' : isComplete ? 'success.main' : 'text.secondary',
										cursor: isAccessible ? 'pointer' : 'default',
										display: 'flex',
										height: 32,
										justifyContent: 'center',
										transition: 'all 0.15s ease',
										width: 32,
										'&:hover': isAccessible ? { opacity: 0.8, transform: 'scale(1.05)' } : {},
									}}>
									{isComplete && !isCurrent ? (
										<IconCheck sx={{ fontSize: 16 }} />
									) : (
										<Typography variant="caption" fontWeight={600}>
											{index + 1}
										</Typography>
									)}
								</Box>
							);
						})}
					</Stack>
				</Stack>
				<LinearProgress
					sx={{
						bgcolor: alpha(theme.palette.primary.main, 0.1),
						borderRadius: 0,
						height: 3,
						mt: 2,
						'& .MuiLinearProgress-bar': { borderRadius: 0 },
					}}
					value={progress}
					variant="determinate"
				/>
			</Box>

			{/* Step label bar */}
			<Box
				sx={{
					bgcolor: 'background.paper',
					borderBottom: `1px solid ${theme.palette.divider}`,
					px: 4,
					py: 1.25,
				}}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Box
						sx={{
							alignItems: 'center',
							border: `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`,
							borderRadius: '50%',
							color: 'primary.main',
							display: 'flex',
							height: 28,
							justifyContent: 'center',
							width: 28,
						}}>
						{currentStepConfig.icon}
					</Box>
					<Typography variant="body2" fontWeight={500}>
						{currentStepConfig.label}
					</Typography>
					<Typography variant="caption" color="text.disabled">
						Step {activeStep + 1} of {STEPS.length}
					</Typography>
				</Stack>
			</Box>

			{/* Error */}
			{error && (
				<Alert severity="error" sx={{ borderRadius: 0 }}>
					{error}
				</Alert>
			)}

			{/* Content */}
			<Box ref={contentRef} sx={{ flex: 1, overflow: 'auto' }}>
				{renderStepContent()}
			</Box>

			{/* Navigation */}
			<Box
				sx={{
					bgcolor: 'background.paper',
					borderTop: `1px solid ${theme.palette.divider}`,
					px: 4,
					py: 1.5,
				}}>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Button
						disabled={isFirstStep}
						onClick={handleBack}
						startIcon={<IconBack />}
						sx={{ color: 'text.secondary' }}>
						Back
					</Button>
					<Stack direction="row" spacing={2}>
						{canSkipToReview && (
							<Button onClick={handleSkipToReview} sx={{ color: 'text.secondary' }}>
								Skip to Review
							</Button>
						)}
						<Button
							disableElevation
							endIcon={isLastStep ? <IconCheck /> : <IconNext />}
							onClick={handleNext}
							sx={{ px: 3 }}
							variant="contained">
							{isLastStep ? 'Create Spend Plan' : 'Continue'}
						</Button>
					</Stack>
				</Stack>
			</Box>
		</Box>
	);
};
